import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "../reminder.module.css";
import MTopBar from "@/components/ui/atoms/MTopBar";
import MHeader from "@/components/ui/organisms/MHeader";
import headerStyles from "@/components/ui/organisms/MHeader.module.css";
import { getMemberInfo } from "@/hooks/useMembers";
import { MButton } from "@/components/ui/atoms/MButtons";
import CriticalReminderToggle from "@/components/ui/molecules/CriticalReminderToggle";
import { useReminders } from "@/hooks/useReminders";
import {
  CreateReminder,
  DayFrequency,
  ReminderFrequency,
  Reminders,
  ReminderType,
  UpdateReminder,
} from "@/types/reminders";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import {
  displayDayFrequencyField,
  displayEventForField,
  displayFrequencyField,
  getMedicationFrequencyText,
  getPossessiveName,
  paintDayFrequencyField,
  paintDosageInput,
  paintDropDownFrequencySelectField,
  paintFrequencyDaysField,
  paintFrequencyDisplayField,
  paintFrequencySpecificDaysField,
  paintReminderDateField,
  paintReminderEndDateField,
  paintReminderNotesField,
  paintReminderTimeField,
  ReminderInputStage,
} from "../remindersHelper";
import ReminderSummaryScreen, {
  ReminderSummaryTimeSlot,
} from "../ReminderSummaryScreen";
import { usePillbox } from "@/hooks/usePillbox";
import { MedicineItemType, MedicineStatus, PillboxItem } from "@/types/pillbox";
import { buildReminderPrefillFromPillboxItem } from "@/pages/pillbox/utils/reminderPrefill";
import {
  formatFrequencyLine,
  formatMedicineTitle,
} from "@/pages/pillbox/utils/formatMedicine";
import MLoader from "@/components/ui/atoms/MLoader";
import { DEFAULT_TIME_12HR, DEFAULT_YEARS_AHEAD } from "@/utils/constants";
import {
  getTimezone,
  initDate,
  isTimeBefore,
  normalizeTo12HourTime,
  updateTime,
} from "@/utils/date-utils";
import { calculateOptimalDosages } from "@/utils/dosage-scheduler";
import { getErrorMessage } from "@/utils/error";
import { addYears, isBefore } from "date-fns";
import MSpacer from "@/components/ui/atoms/MSpacer";
import { useKeyboardScrollToTop } from "@/hooks/useKeyboardScrollToTop";
import { useFixMobileKeyboard } from "@/hooks/useFixMobileKeyboard";
import MEmptyCard from "@/components/ui/organisms/MEmptyCard";
import {
  IconChevronRight,
  IconFileOff,
  IconLeaf,
  IconPillFilled,
  IconPlus,
} from "@tabler/icons-react";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
} from "@/constants/analytics.constants";
import { useIsDesktopPWA } from "@/hooks/useIsDesktopPWA";

/**
 * Insurance Reminder Creation Page
 * Allows users to select an insurance type to create a reminder for
 */
const MedicationReminder: React.FC = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id;
  const isDesktopPWA = useIsDesktopPWA();

  // Get reminder data for editing - memoize to prevent unexpected changes
  const reminder: Reminders = location.state?.reminder;
  const isEditing = useMemo(() => {
    const result = !!reminder;
    return result;
  }, [reminder]);

  // Pillbox's "Add Reminder" option (medicine detail page) sends a partial
  // Reminders shape here to prefill this form for a NEW reminder — distinct
  // from `reminder` above, which is a full existing Reminders record and
  // switches the whole page into edit mode. `prefillData` only seeds the
  // initial state below; it must never affect `isEditing`.
  const pillboxPrefill: Partial<Reminders> | undefined =
    location.state?.pillboxPrefill;
  const prefillData = reminder ?? pillboxPrefill;

  // Initialize state with reminder data if editing, or Pillbox prefill data
  // if arriving from Pillbox's "Add Reminder" action.
  const [reminderFor, setReminderFor] = useState<string | null>(
    prefillData?.reminderFor ?? null
  );
  const [stage, setStage] = useState<ReminderInputStage>(
    isEditing || !!pillboxPrefill
      ? ReminderInputStage.ADD
      : ReminderInputStage.START
  );
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: boolean;
  }>({});
  const [showValidationErrors, setShowValidationErrors] =
    useState<boolean>(false);
  const [frequency, setFrequency] = useState<ReminderFrequency | null>(
    prefillData?.frequency ?? null
  );
  const [frequencyValue, setFrequencyValue] = useState<number | null>(
    prefillData?.frequencyValue ?? null
  );

  // Initialize dosage times from reminder data if available. Every value is
  // pushed through normalizeTo12HourTime first: dayFrequencyValue can hold
  // 24-hour "HH:mm" (Pillbox slot format, copied over verbatim by older
  // pillbox → reminder syncs) or legacy seconds, neither of which the time
  // pickers or isTimeBefore understand — an un-normalised "13:00" reads as
  // midnight to the validators while the picker shows something else again.
  const dosageAt = (index: number): string | null =>
    normalizeTo12HourTime(prefillData?.dayFrequencyValue?.[index] ?? null);

  const [firstDosage, setFirstDosage] = useState<string | null>(
    dosageAt(0) ?? DEFAULT_TIME_12HR
  );
  const [secondDosage, setSecondDosage] = useState<string | null>(dosageAt(1));
  const [thirdDosage, setThirdDosage] = useState<string | null>(dosageAt(2));
  const [fourthDosage, setFourthDosage] = useState<string | null>(dosageAt(3));
  const [showDayFrequencyDrawer, setShowDayFrequencyDrawer] =
    useState<boolean>(false);
  const [dayFrequency, setDayFrequency] = useState<DayFrequency | null>(
    prefillData?.dayFrequency ?? null
  );
  const [reminderDate, setReminderDate] = useState<Date | null>(
    initDate(prefillData?.startDate)
  );
  const [reminderEndDate, setReminderEndDate] = useState<Date | null>(
    initDate(prefillData?.endDate)
  );
  const [selectedDays, setSelectedDays] = useState<string[]>(
    prefillData?.selectedDays?.map((day) => day.toString()) ?? []
  );
  const [showSetDays, setShowSetDays] = useState<boolean>(false);
  const [notes, setNotes] = useState<string | null>(prefillData?.notes ?? null);
  // Only an EXISTING reminder's own tier should ever default this to
  // non-critical — a legacy reminder with no tier set must stay
  // non-critical, not fall through to the "brand new reminder" default of
  // true. pillboxPrefill never carries a tier, so it always gets `true`
  // here regardless, same as the plain new-reminder case.
  const [isCritical, setIsCritical] = useState<boolean>(
    reminder ? reminder.tier === "CRITICAL" : true
  );
  const [stageStack, setStageStack] = useState<ReminderInputStage[]>([
    ReminderInputStage.START,
  ]);
  // Links the created reminder back to the Pillbox medicine it's for (see
  // MedicineDetail.tsx, which looks reminders up by sourceId). Seeded from
  // pillboxPrefill for the "Add Reminder" entry point; set directly by
  // handleMedicationSelect for the in-page picker below.
  const [sourceId, setSourceId] = useState<string | null>(
    pillboxPrefill?.sourceId ?? null
  );
  // Only known for medicines picked via the in-page list below (which has
  // the full PillboxItem) — defaults to the pill icon for the pillboxPrefill/
  // isEditing entry points, which only carry a Partial<Reminders>.
  const [isSupplementItem, setIsSupplementItem] = useState(false);
  const { addReminder, updateReminder, totalCount, fetchReminders } =
    useReminders(memberInfo.id);

  // Use keyboard scroll-to-top hook
  // useKeyboardScrollToTop([stage, reminderFor]);

  // Fix mobile keyboard issues
  useFixMobileKeyboard();

  // Fetch reminder count on mount
  useEffect(() => {
    fetchReminders();
  }, []);

  // Medicines come from Pillbox itself (not the legacy Health Profile
  // /medications API, which no longer even carries frequency/schedule data
  // — see MemberMedication schema comments) so status filtering and the
  // frequency/dose-time prefill both come from the same source of truth
  // already used by Pillbox's own "Add Reminder" action.
  const { fetchLedger } = usePillbox(memberId);
  const [medications, setMedications] = useState<PillboxItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  useEffect(() => {
    if (fetched) return;
    setLoading(true);
    fetchLedger(MedicineStatus.ACTIVE)
      .then(setMedications)
      .finally(() => {
        setLoading(false);
        setFetched(true);
      });
  }, [fetched, fetchLedger]);

  const memberName = getPossessiveName(memberInfo, t);
  const groupedMedications = useMemo(() => {
    const groups = new Map<string, PillboxItem[]>();
    medications
      // Only medicines a reminder can actually be built for — SOS/as-needed
      // items and every-N-hours dosing have no equivalent in the Reminders
      // module's frequency model (see buildReminderPrefillFromPillboxItem).
      .filter((item) => !!buildReminderPrefillFromPillboxItem(item))
      .sort((a, b) =>
        formatMedicineTitle(a).localeCompare(formatMedicineTitle(b))
      )
      .forEach((item) => {
        const letter = formatMedicineTitle(item).charAt(0).toUpperCase();
        if (!groups.has(letter)) groups.set(letter, []);
        groups.get(letter)!.push(item);
      });
    return Array.from(groups.entries());
  }, [medications]);
  const [showFrequencyDrawer, setShowFrequencyDrawer] =
    useState<boolean>(false);

  // Calculate exitPath early - before any early returns (for use in all render paths)
  const exitPath = pillboxPrefill
    ? "/pillbox"
    : stage === ReminderInputStage.START
    ? ""
    : "/reminders";

  // Track page load once
  useEffect(() => {
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
      screenName: ANALYTICS_SCREEN_NAMES.REMINDER_ADD_MEDICATION,
    });
  }, []);

  const validateCurrentStage = (): boolean => {
    let isValid = true;
    const newErrors: { [key: string]: boolean } = {};

    if (stage === ReminderInputStage.DETAILS) {
      // Validate provider is selected
      if (!reminderFor) {
        newErrors.reminderFor = true;
        isValid = false;
      }
    }

    setValidationErrors(newErrors);
    setShowValidationErrors(!isValid);
    return isValid;
  };

  // Show toast message for error notification
  const showErrorToast = (_: string) => {
    showToast({
      message: t("errors.toast"),
      variant: "error",
    });
  };

  const handleBack = () => {
    // Set only when opened from a medicine's Pillbox detail page (Add
    // Reminder or opening its already-linked reminder — see
    // MedicineDetail's handleAddReminder/handleOpenReminder) — return there
    // instead of the generic destinations below, which would otherwise skip
    // past the screen this was actually opened from.
    const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;
    if (returnTo) {
      navigate(returnTo);
      return;
    }

    // If editing, go back to reminders list directly
    if (isEditing) {
      navigate("/reminders");
      return;
    }

    // Arrived via Pillbox's "Add Reminder" prefill, which jumps straight to
    // the ADD (summary) stage without pushing onto stageStack — so there's
    // no wizard step to pop back through. Return to Pillbox instead.
    if (pillboxPrefill) {
      navigate("/pillbox");
      return;
    }

    let nextStage = stageStack.pop();
    if (stage === nextStage) {
      nextStage = stageStack.pop();
    }

    if (stageStack.length === 0) {
      stageStack.push(ReminderInputStage.START);
    }

    if (nextStage !== undefined) {
      setStage(nextStage);
      setStageStack([...stageStack]);
    } else {
      // Truly nothing left in the wizard's own stage stack to pop back
      // through — navigate(-1) (browser history) is unreliable here since
      // other screens (e.g. AddMedicine's returnTo) push new entries rather
      // than popping, which turned this into an infinite ping-pong between
      // this screen and wherever the history happened to point. Always land
      // on the reminders listing instead — the one deterministic "start" of
      // this flow, regardless of how this screen was actually reached.
      navigate("/reminders");
    }
  };

  const nextReminderInputStage = (): ReminderInputStage =>
    dayFrequency === DayFrequency.ONCE
      ? ReminderInputStage.REMINDER_DETAILS
      : ReminderInputStage.REMINDER_SCHEDULE;

  const nextStage = () => {
    let nextStage: ReminderInputStage;
    switch (stage) {
      case ReminderInputStage.START:
        nextStage = ReminderInputStage.FREQUENCY_DETAILS;
        break;
      case ReminderInputStage.FREQUENCY_DETAILS:
        switch (frequency) {
          case ReminderFrequency.SPECIFIC_DAYS:
            nextStage = ReminderInputStage.DAY_FREQUENCY;
            break;
          case ReminderFrequency.DAY_FREQUENCY:
            nextStage = ReminderInputStage.DAY_INPUT;
            break;
          default:
            nextStage = nextReminderInputStage();
            break;
        }
        break;
      case ReminderInputStage.DAY_FREQUENCY:
        nextStage = nextReminderInputStage();
        break;
      case ReminderInputStage.DAY_INPUT:
        nextStage = nextReminderInputStage();
        break;
      case ReminderInputStage.REMINDER_SCHEDULE:
        nextStage = ReminderInputStage.REMINDER_DETAILS;
        break;
      case ReminderInputStage.REMINDER_DETAILS:
        nextStage = ReminderInputStage.ADD;
        break;
      default:
        showErrorToast(t("errors.maxStageReached"));
        return;
    }

    if (validateCurrentStage()) {
      stageStack.push(nextStage);
      setStage(nextStage);
      setStageStack([...stageStack]);
      setShowValidationErrors(false);

      // Track stage changes
      if (nextStage === ReminderInputStage.FREQUENCY_DETAILS) {
        trackUserDataLoaded({
          memberInfo,
          screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
          screenName: ANALYTICS_SCREEN_NAMES.REMINDER_ADD_MEDICATION_FREQUENCY,
        });
      } else if (nextStage === ReminderInputStage.REMINDER_SCHEDULE) {
        trackUserDataLoaded({
          memberInfo,
          screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
          screenName: ANALYTICS_SCREEN_NAMES.REMINDER_ADD_MEDICATION_TIME,
        });
      } else if (nextStage === ReminderInputStage.REMINDER_DETAILS) {
        trackUserDataLoaded({
          memberInfo,
          screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
          screenName: ANALYTICS_SCREEN_NAMES.REMINDER_ADD_MEDICATION_DATE,
        });
      } else if (nextStage === ReminderInputStage.ADD) {
        trackUserDataLoaded({
          memberInfo,
          screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
          screenName: ANALYTICS_SCREEN_NAMES.REMINDER_ADD_MEDICATION_SUMMARY,
        });
      }

      // Auto-scroll to top when moving to next stage
      setTimeout(() => {
        document
          .querySelector("main")
          ?.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    }
  };

  const handleMedicationSelect = (item: PillboxItem) => {
    // All meaningful fields already live on the Pillbox medicine itself —
    // this reminder flow only needs the reminder date/time on top, so tapping
    // a medicine jumps straight to the summary screen (ADD) with everything
    // else prefilled, instead of walking through the frequency wizard again.
    const prefill = buildReminderPrefillFromPillboxItem(item);
    if (!prefill) return;

    setReminderFor(prefill.reminderFor ?? null);
    setFrequency(prefill.frequency ?? null);
    setFrequencyValue(prefill.frequencyValue ?? null);
    setSelectedDays(prefill.selectedDays?.map(String) ?? []);
    setDayFrequency(prefill.dayFrequency ?? null);
    const [d1, d2, d3, d4] = prefill.dayFrequencyValue ?? [];
    setFirstDosage(d1 ?? null);
    setSecondDosage(d2 ?? null);
    setThirdDosage(d3 ?? null);
    setFourthDosage(d4 ?? null);
    setReminderDate(prefill.startDate ?? new Date());
    setReminderEndDate(prefill.endDate ?? null);
    setNotes(prefill.notes ?? null);
    setSourceId(prefill.sourceId ?? null);
    setIsSupplementItem(item.itemType === MedicineItemType.SUPPLEMENT);

    setStageStack([ReminderInputStage.START, ReminderInputStage.ADD]);
    setStage(ReminderInputStage.ADD);
    setTimeout(() => {
      document.querySelector("main")?.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
  };

  // Figma 5257-62389/62507: a flat list of the member's own saved Pillbox
  // medicines, grouped alphabetically — reminders can only be set for a
  // medicine already saved there, so there's no free-text/typeahead entry
  // here anymore (that used to let you type an unsaved name).
  const paintMedicationsSelect = () => {
    if (groupedMedications.length === 0) {
      return (
        <MEmptyCard
          icon={<IconFileOff size={64} stroke={2} color="#AD95D1" />}
          primaryTitle={t("reminders.medication.noMedicinesSaved")}
          secondaryTitle={t("reminders.medication.noMedicinesSavedDescription")}
          primaryCTA={{
            text: t("reminders.medication.addMedicationCta"),
            leftIcon: <IconPlus size={16} />,
            // returnTo tells AddMedicine's Back button (and its post-save
            // navigation) to come back here instead of falling through to
            // the Pillbox landing screen — see AddMedicineNavState.returnTo.
            onClick: () =>
              navigate("/pillbox/add-medicine", {
                state: { returnTo: "/reminders/medication" },
              }),
          }}
        />
      );
    }

    return (
      <div className={styles.medicineListSections}>
        {groupedMedications.map(([letter, items]) => (
          <div key={letter} className={styles.medicineSection}>
            <div className={styles.medicineSectionHeader}>
              <span>{letter}</span>
              <div className={styles.medicineSectionDivider} />
            </div>
            {items.map((item) => {
              const isSupplement =
                item.itemType === MedicineItemType.SUPPLEMENT;
              return (
                <button
                  key={item.id}
                  type="button"
                  className={styles.medicineListCard}
                  onClick={() => handleMedicationSelect(item)}
                >
                  <div
                    className={`${styles.typeIconBox} ${
                      isSupplement
                        ? styles.typeIconGradientSupplement
                        : styles.typeIconGradientDoctor
                    }`}
                  >
                    {isSupplement ? (
                      <IconLeaf size={24} stroke={1.5} color="#8764BB" />
                    ) : (
                      <IconPillFilled size={24} color="#8764BB" />
                    )}
                  </div>
                  <div className={styles.medicineListCardText}>
                    <span className={styles.medicineListCardName}>
                      {formatMedicineTitle(item)}
                      <IconChevronRight size={12} />
                    </span>
                    <span className={styles.medicineListCardFrequency}>
                      {formatFrequencyLine(item, item.recurrenceIntervalDays)}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  const reminderDateField = (
    labelKey: string = "THERAPY",
    isSummaryDisplay: boolean = false
  ) => {
    return paintReminderDateField(
      reminderDate,
      setReminderDate,
      addYears(new Date(), DEFAULT_YEARS_AHEAD),
      t,
      labelKey,
      true,
      isSummaryDisplay
    );
  };

  const reminderEndDateField = (isSummaryDisplay: boolean = false) => {
    return paintReminderEndDateField(
      reminderEndDate,
      setReminderEndDate,
      addYears(new Date(), DEFAULT_YEARS_AHEAD),
      reminderDate,
      t,
      isSummaryDisplay ? "add" : undefined,
      true,
      isSummaryDisplay
    );
  };

  const reminderNotesField = (isSummaryDisplay: boolean = false) => {
    return paintReminderNotesField(
      notes,
      setNotes,
      t,
      undefined,
      isSummaryDisplay
    );
  };

  const reminderTimeField = (isSummaryDisplay: boolean = false) => {
    return paintReminderTimeField(
      firstDosage,
      setFirstDosage,
      reminderDate,
      t,
      undefined,
      undefined,
      isSummaryDisplay
    );
  };

  // pass a function to check for disable state
  const paintNextButton = (disableCheck: () => boolean) => {
    return (
      <MButton
        variant="filled"
        size="lg"
        onClick={() => nextStage()}
        className={styles.nextButton}
        disabled={disableCheck()}
      >
        {t("common.next")}
      </MButton>
    );
  };

  const paintDayInputDetails = () => {
    return paintFrequencyDaysField(
      frequencyValue,
      setFrequencyValue,
      t,
      "medication"
    );
  };

  const isDayInputInvalid = (): boolean => {
    return !frequencyValue || frequencyValue < 1;
  };

  const isReminderDetailsInvalid = (): boolean => {
    if (!reminderDate) {
      return true;
    }
    if (reminderEndDate && isBefore(reminderEndDate, reminderDate)) {
      return true;
    }
    // Reminder time is mandatory when dayFrequency is ONCE
    if (dayFrequency === DayFrequency.ONCE && !firstDosage) {
      return true;
    }
    return false;
  };

  const isReminderScheduleInvalid = (): boolean => {
    switch (dayFrequency) {
      case DayFrequency.ONCE:
        return false;
      case DayFrequency.TWICE:
        return (
          !firstDosage ||
          !secondDosage ||
          isTimeBefore(secondDosage, firstDosage)
        );
      case DayFrequency.THRICE:
        return (
          !firstDosage ||
          !secondDosage ||
          !thirdDosage ||
          isTimeBefore(secondDosage, firstDosage) ||
          isTimeBefore(thirdDosage, secondDosage)
        );
      case DayFrequency.FOUR_TIMES:
        return (
          !firstDosage ||
          !secondDosage ||
          !thirdDosage ||
          !fourthDosage ||
          isTimeBefore(secondDosage, firstDosage) ||
          isTimeBefore(thirdDosage, secondDosage) ||
          isTimeBefore(fourthDosage, thirdDosage)
        );
      default:
        return true;
    }
  };

  // Effect to initialize additional dosage times when first dosage is set.
  // Also fires on ADD (not just REMINDER_SCHEDULE) — editing an existing
  // reminder and picking a medicine from Pillbox both land directly on ADD,
  // skipping REMINDER_SCHEDULE entirely, and a slot count mismatch there
  // (e.g. dayFrequency says FOUR_TIMES but only 2 dose times ever got
  // saved) would otherwise leave second/third/fourthDosage stuck null.
  // A null slot reaching MTimePicker falls back to the current wall-clock
  // time (rounded to the nearest minuteStep) instead of a sensible default —
  // this is what was producing the "7:30/2:30/etc." reports.
  useEffect(() => {
    if (
      firstDosage &&
      (stage === ReminderInputStage.REMINDER_SCHEDULE ||
        stage === ReminderInputStage.ADD)
    ) {
      // Only calculate if we need to set dosages
      if (
        !secondDosage ||
        (dayFrequency === DayFrequency.THRICE && !thirdDosage) ||
        (dayFrequency === DayFrequency.FOUR_TIMES &&
          (!thirdDosage || !fourthDosage))
      ) {
        // Calculate optimal dosage times using the utility function
        const dosages = calculateOptimalDosages(dayFrequency);

        // These are fixed clock times (9/1/5/9), not computed relative to
        // firstDosage — if the user has set an unusually late first dose
        // (e.g. 10pm for twice a day), a fixed default can land BEFORE it.
        // isReminderScheduleInvalid below rejects that combination, so
        // filling it in anyway would silently block submission on a field
        // the user never touched. Only accept a default that keeps
        // chronological order intact against whatever the previous slot
        // actually ends up being; otherwise leave it blank so the user
        // fills it in themselves instead of hitting an invisible dead end.
        let previousSlot = firstDosage;

        let effectiveSecond = secondDosage;
        if (
          !secondDosage &&
          dosages.secondDosage &&
          !isTimeBefore(dosages.secondDosage, previousSlot)
        ) {
          setSecondDosage(dosages.secondDosage);
          effectiveSecond = dosages.secondDosage;
        }
        previousSlot = effectiveSecond ?? previousSlot;

        let effectiveThird = thirdDosage;
        if (
          !thirdDosage &&
          dosages.thirdDosage &&
          effectiveSecond &&
          !isTimeBefore(dosages.thirdDosage, previousSlot)
        ) {
          setThirdDosage(dosages.thirdDosage);
          effectiveThird = dosages.thirdDosage;
        }
        previousSlot = effectiveThird ?? previousSlot;

        if (
          !fourthDosage &&
          dosages.fourthDosage &&
          effectiveThird &&
          !isTimeBefore(dosages.fourthDosage, previousSlot)
        ) {
          setFourthDosage(dosages.fourthDosage);
        }
      }
    }
  }, [
    firstDosage,
    stage,
    dayFrequency,
    secondDosage,
    thirdDosage,
    fourthDosage,
  ]);

  const paintReminderSchedules = () => {
    const retArr = [];
    switch (dayFrequency) {
      case DayFrequency.TWICE:
        retArr.push(
          paintDosageInput(firstDosage, setFirstDosage, t, true, "first")
        );
        retArr.push(
          paintDosageInput(secondDosage, setSecondDosage, t, true, "second")
        );
        break;
      case DayFrequency.THRICE:
        retArr.push(
          paintDosageInput(firstDosage, setFirstDosage, t, true, "first")
        );
        retArr.push(
          paintDosageInput(secondDosage, setSecondDosage, t, true, "second")
        );
        retArr.push(
          paintDosageInput(thirdDosage, setThirdDosage, t, true, "third")
        );
        break;
      case DayFrequency.FOUR_TIMES:
        retArr.push(
          paintDosageInput(firstDosage, setFirstDosage, t, true, "first")
        );
        retArr.push(
          paintDosageInput(secondDosage, setSecondDosage, t, true, "second")
        );
        retArr.push(
          paintDosageInput(thirdDosage, setThirdDosage, t, true, "third")
        );
        retArr.push(
          paintDosageInput(fourthDosage, setFourthDosage, t, true, "fourth")
        );
        break;
      default:
        retArr.push(paintDosageInput(firstDosage, setFirstDosage, t, false));
        break;
    }
    return retArr;
  };

  const paintReminderDetails = () => {
    const retArr = [];
    retArr.push(reminderDateField());
    retArr.push(reminderEndDateField());
    if (dayFrequency === DayFrequency.ONCE) {
      retArr.push(reminderTimeField());
    }
    retArr.push(reminderNotesField());
    return retArr;
  };

  const paintFrequencyDetails = () => {
    const retArr = [];
    retArr.push(
      paintDropDownFrequencySelectField(
        frequency,
        setFrequency,
        showFrequencyDrawer,
        setShowFrequencyDrawer,
        t,
        undefined,
        // Medication does not support one-off; therapy-only flow.
        Object.values(ReminderFrequency).filter(
          (f) => f !== ReminderFrequency.ONLY_ONCE
        )
      )
    );
    retArr.push(
      paintDayFrequencyField(
        dayFrequency,
        setDayFrequency,
        showDayFrequencyDrawer,
        setShowDayFrequencyDrawer,
        t
      )
    );
    return retArr;
  };

  const isFrequencyDetailsInvalid = (): boolean => {
    return !frequency || !dayFrequency;
  };

  const paintDayFrequencyInputs = () => {
    const retArr = [];
    retArr.push(
      paintFrequencySpecificDaysField(
        selectedDays,
        setShowSetDays,
        showSetDays,
        handleSetDaysSelect,
        t,
        "medication"
      )
    );
    return retArr;
  };

  const handleSetDaysSelect = (options: string[]) => {
    setSelectedDays(options);
    setShowSetDays(false);
  };

  const isDayFrequencyInvalid = (): boolean => {
    return !dayFrequency;
  };

  const isAddInvalid = (): boolean => {
    return isReminderDetailsInvalid() || isReminderScheduleInvalid();
  };

  const paintHeader = (
    title: string,
    description?: string,
    addSpacer: boolean = true
  ) => {
    return (
      <>
        <MHeader
          module="reminders"
          memberInfo={memberInfo}
          title={title}
          description={description ? description : undefined}
          useParentGradient={false}
          className={
            isFinalStage
              ? headerStyles.stickyBelowTopBarWithGradient
              : headerStyles.stickyBelowTopBar
          }
        />
        {/* MHeader's own .headerContainer already contributes 16px of
            padding-bottom below the description — this spacer makes up the
            rest of the standardized 40px header-to-body gap used across
            every reminders screen (except the listing screen); the sticky
            header needs extra clearance, but only on the desktop app. */}
        {addSpacer && <MSpacer mt={isDesktopPWA ? 54 : 24} />}
      </>
    );
  };

  const handleCreateUpdateReminder = async () => {
    const firstDosageValue = firstDosage || DEFAULT_TIME_12HR;
    const startDate = updateTime(reminderDate, firstDosageValue);
    const endDate = reminderEndDate
      ? updateTime(reminderEndDate, firstDosageValue)
      : null;

    const timezone = getTimezone();
    // Create base reminder data
    const baseReminderData = {
      type: ReminderType.MEDICATION,
      startDate,
      endDate,
      reminderFor,
      frequency,
      notes,
      timezone,
      tier: isCritical ? ("CRITICAL" as const) : ("ROUTINE" as const),
    };

    // Add frequency-specific properties based on the frequency type
    const reminderData: any = { ...baseReminderData };

    if (frequency === ReminderFrequency.DAY_FREQUENCY) {
      reminderData.frequencyValue = frequencyValue;
    } else if (frequency === ReminderFrequency.SPECIFIC_DAYS) {
      reminderData.selectedDays = selectedDays.map((day) => Number(day));
    }

    reminderData.dayFrequency = dayFrequency;
    switch (dayFrequency) {
      case DayFrequency.ONCE:
        reminderData.dayFrequencyValue = [firstDosageValue];
        break;
      case DayFrequency.TWICE:
        reminderData.dayFrequencyValue = [firstDosageValue, secondDosage];
        break;
      case DayFrequency.THRICE:
        reminderData.dayFrequencyValue = [
          firstDosageValue,
          secondDosage,
          thirdDosage,
        ];
        break;
      case DayFrequency.FOUR_TIMES:
        reminderData.dayFrequencyValue = [
          firstDosageValue,
          secondDosage,
          thirdDosage,
          fourthDosage,
        ];
        break;
    }

    if (isEditing && reminder?.id) {
      // Update existing reminder
      const updatedReminder: UpdateReminder = reminderData;
      await updateReminder(reminder.id, updatedReminder);
    } else {
      // Check if this is first reminder BEFORE adding
      const isFirstReminder = totalCount === 0;

      try {
        // Create new reminder — link back to the originating Pillbox
        // medicine (whether arriving via its "Add Reminder" prefill or
        // picked from the in-page list above), so the medicine detail page
        // can find and display this reminder later.
        if (sourceId) {
          reminderData.sourceId = sourceId;
        }
        const newReminder: CreateReminder = reminderData;
        await addReminder(newReminder);

        // Track reminder completion with first reminder flag
        trackUserDataLoaded({
          memberInfo,
          screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
          screenName: ANALYTICS_SCREEN_NAMES.REMINDER_ADD_MEDICATION_COMPLETE,
          ...(isFirstReminder && { action: "first_reminder_added" }),
        });
      } catch (err) {
        const errorMessage = getErrorMessage(err, "Failed to create reminder");
        showErrorToast(errorMessage);
        return;
      }
    }

    // Track reminder completion for edit case
    if (isEditing) {
      trackUserDataLoaded({
        memberInfo,
        screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
        screenName: ANALYTICS_SCREEN_NAMES.REMINDER_ADD_MEDICATION_COMPLETE,
      });
    }

    // Opened from a Pillbox medicine's detail page (Add Reminder or its
    // already-linked reminder) — return there instead of the generic
    // reminders list, matching handleBack's same returnTo check above.
    const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;
    navigate(returnTo || "/reminders");
  };

  // Show loader while fetching medications
  if (medications.length === 0 && loading) {
    return (
      <div className="flex flex-col min-h-screen relative">
        <MTopBar
          hideProfilePicker
          exitPath={exitPath}
          onBack={handleBack}
          withBackground
        />
        <div className="profile-page-root flex items-center justify-center">
          <MLoader />
        </div>
      </div>
    );
  }

  const isFinalStage = stage === ReminderInputStage.ADD || isEditing;

  const dosageBySlot: ReminderSummaryTimeSlot[] = [
    { value: firstDosage, onChange: setFirstDosage },
    { value: secondDosage, onChange: setSecondDosage },
    { value: thirdDosage, onChange: setThirdDosage },
    { value: fourthDosage, onChange: setFourthDosage },
  ];
  const slotCount =
    dayFrequency === DayFrequency.TWICE
      ? 2
      : dayFrequency === DayFrequency.THRICE
      ? 3
      : dayFrequency === DayFrequency.FOUR_TIMES
      ? 4
      : 1;
  return (
    <div
      className={`flex flex-col min-h-screen relative pb-[24px] ${
        isFinalStage ? styles.addStageBackgroundFinal : ""
      }`}
    >
      {/* Sticky wrapper so the topbar scrolls together with the sticky
          MHeader below it instead of scrolling away on its own. */}
      <div className="sticky top-0 z-[31]">
        <MTopBar
          hideProfilePicker
          exitPath={exitPath}
          onBack={handleBack}
          withBackground={!isFinalStage}
          showPurpleGradient={isFinalStage}
        />
      </div>
      <div
        className="profile-page-root flex flex-col"
        style={{ minHeight: "calc(var(--vh, 1vh) * 100 - 128px)" }}
      >
        <div className={styles.content}>
          {stage === ReminderInputStage.START && (
            <div className={styles.cardContainer}>
              <div className="relative w-full h-full flex flex-col">
                {paintHeader(
                  t("reminders.medication.selectPrompt", { name: memberName }),
                  t("reminders.medication.selectDescription")
                )}
                <div className="profile-input-container flex-1">
                  {paintMedicationsSelect()}
                </div>
              </div>
            </div>
          )}
          {stage === ReminderInputStage.FREQUENCY_DETAILS && (
            <div className="relative w-full h-full flex flex-col">
              {paintHeader(
                t("reminders.medication.detailsPrompt"),
                t("reminders.medication.detailsDescription")
              )}
              <div className="profile-input-container flex-1">
                {paintFrequencyDetails()}
              </div>
              {/* Next button at the bottom right - following Figma design */}
              <div className="confirm-footer pt-0">
                {paintNextButton(isFrequencyDetailsInvalid)}
              </div>
            </div>
          )}
          {stage === ReminderInputStage.DAY_FREQUENCY && (
            <div className="relative w-full h-full flex flex-col">
              {paintHeader(
                t("reminders.medication.dayFrequencyPrompt"),
                t("reminders.medication.dayFrequencyDescription")
              )}
              <div className="profile-input-container flex-1">
                {paintDayFrequencyInputs()}
              </div>
              <div className="confirm-footer pt-0">
                {paintNextButton(isDayFrequencyInvalid)}
              </div>
            </div>
          )}
          {stage === ReminderInputStage.DAY_INPUT && (
            <div className="relative w-full h-full flex flex-col">
              {paintHeader(
                t("reminders.medication.dayInputPrompt"),
                t("reminders.medication.dayInputDescription")
              )}
              <div className="profile-input-container flex-1">
                {paintDayInputDetails()}
              </div>
              {/* Next button at the bottom right - following Figma design */}
              <div className="confirm-footer pt-0">
                {paintNextButton(isDayInputInvalid)}
              </div>
            </div>
          )}
          {stage === ReminderInputStage.REMINDER_SCHEDULE && (
            <div className="relative w-full h-full flex flex-col">
              {paintHeader(
                t("reminders.medication.reminderSchedulePrompt"),
                t("reminders.medication.reminderScheduleDescription")
              )}
              <div className="profile-input-container flex-1">
                {paintReminderSchedules()}
              </div>
              {/* Next button at the bottom right - following Figma design */}
              <div className="confirm-footer pt-0">
                {paintNextButton(isReminderScheduleInvalid)}
              </div>
            </div>
          )}
          {stage === ReminderInputStage.REMINDER_DETAILS && (
            <div className="relative w-full h-full flex flex-col">
              {paintHeader(
                t(
                  `reminders.medication.reminderDetailsPrompt${
                    dayFrequency !== DayFrequency.ONCE ? ".dateOnly" : ""
                  }`
                ),
                t(
                  `reminders.medication.reminderDetailsDescription${
                    dayFrequency !== DayFrequency.ONCE ? ".dateOnly" : ""
                  }`
                )
              )}
              <div className="profile-input-container flex-1">
                {paintReminderDetails()}
              </div>
              {/* Next button at the bottom right - following Figma design */}
              <div className="confirm-footer pt-0">
                {paintNextButton(isReminderDetailsInvalid)}
              </div>
            </div>
          )}
          {stage === ReminderInputStage.ADD && (
            <ReminderSummaryScreen
              memberInfo={memberInfo}
              title={`${
                isEditing ? t("reminders.medication.editTitlePrefix") : ""
              }${
                isEditing ? getPossessiveName(memberInfo, t, true) : memberName
              } medication reminder`}
              description={t(
                isEditing
                  ? "reminders.medication.editPrompt"
                  : "reminders.appointment.summaryScreenDescription"
              )}
              labelKey="medication.add"
              recapIcon={
                isSupplementItem ? (
                  <IconLeaf size={24} stroke={1.5} color="#8764BB" />
                ) : (
                  <IconPillFilled size={24} color="#8764BB" />
                )
              }
              recapIconClass={
                isSupplementItem
                  ? styles.typeIconGradientSupplement
                  : styles.typeIconGradientDoctor
              }
              recapLabel={reminderFor || ""}
              recapSecondaryText={getMedicationFrequencyText(
                frequency,
                selectedDays,
                frequencyValue,
                dayFrequency,
                t
              )}
              topContent={
                <CriticalReminderToggle
                  checked={isCritical}
                  onChange={setIsCritical}
                />
              }
              reminderDate={reminderDate}
              setReminderDate={setReminderDate}
              showEndDate={false}
              reminderEndDate={reminderEndDate}
              setReminderEndDate={setReminderEndDate}
              timeSlots={dosageBySlot.slice(0, slotCount)}
              notes={notes}
              setNotes={setNotes}
              isInvalid={isAddInvalid()}
              onSubmit={handleCreateUpdateReminder}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default MedicationReminder;
